import {
    AppwriteException,
    Client,
    type Payload,
    applyDefaults,
} from '../client';
import type { Models } from '../models';

import { BlockResourceType } from '../enums/block-resource-type';
import { BlockMode } from '../enums/block-mode';
import { Region } from '../enums/region';
import { CacheTarget } from '../enums/cache-target';
import { CacheDatabase } from '../enums/cache-database';
export class Manager {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * Creates a new resource block.
     *
     * @param {string} params.projectId - Project ID
     * @param {BlockResourceType} params.resourceType - Resource type to block (e.g., projects, functions, databases, storage, etc.)
     * @param {string} params.resourceId - Optional resource ID (if omitted, all resources of this type will be blocked)
     * @param {BlockMode} params.mode - Block mode. Use full to block reads and writes, or readOnly to block database writes only.
     * @param {string} params.reason - Optional reason why the resource is blocked
     * @param {string} params.expiredAt - Optional expiration date for the block
     * @throws {AppwriteException}
     * @returns {Promise<Models.ManagerBlock>}
     */
    createBlock(params: {
        projectId: string;
        resourceType: BlockResourceType;
        resourceId?: string;
        mode?: BlockMode;
        reason?: string;
        expiredAt?: string;
    }): Promise<Models.ManagerBlock>;
    /**
     * Creates a new resource block.
     *
     * @param {string} projectId - Project ID
     * @param {BlockResourceType} resourceType - Resource type to block (e.g., projects, functions, databases, storage, etc.)
     * @param {string} resourceId - Optional resource ID (if omitted, all resources of this type will be blocked)
     * @param {BlockMode} mode - Block mode. Use full to block reads and writes, or readOnly to block database writes only.
     * @param {string} reason - Optional reason why the resource is blocked
     * @param {string} expiredAt - Optional expiration date for the block
     * @throws {AppwriteException}
     * @returns {Promise<Models.ManagerBlock>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createBlock(
        projectId: string,
        resourceType: BlockResourceType,
        resourceId?: string,
        mode?: BlockMode,
        reason?: string,
        expiredAt?: string,
    ): Promise<Models.ManagerBlock>;
    createBlock(
        paramsOrFirst:
            | {
                  projectId: string;
                  resourceType: BlockResourceType;
                  resourceId?: string;
                  mode?: BlockMode;
                  reason?: string;
                  expiredAt?: string;
              }
            | string,
        ...rest: [BlockResourceType?, string?, BlockMode?, string?, string?]
    ): Promise<Models.ManagerBlock> {
        let params: {
            projectId: string;
            resourceType: BlockResourceType;
            resourceId?: string;
            mode?: BlockMode;
            reason?: string;
            expiredAt?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                projectId: string;
                resourceType: BlockResourceType;
                resourceId?: string;
                mode?: BlockMode;
                reason?: string;
                expiredAt?: string;
            };
        } else {
            params = {
                projectId: paramsOrFirst as string,
                resourceType: rest[0] as BlockResourceType,
                resourceId: rest[1] as string,
                mode: rest[2] as BlockMode,
                reason: rest[3] as string,
                expiredAt: rest[4] as string,
            };
        }

        const projectId = params.projectId;
        const resourceType = params.resourceType;
        const resourceId = params.resourceId;
        const mode = params.mode;
        const reason = params.reason;
        const expiredAt = params.expiredAt;

        if (typeof projectId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        if (typeof resourceType === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "resourceType"',
            );
        }
        const apiPath = '/manager/blocks';
        const apiPayload: Payload = {};
        if (typeof projectId !== 'undefined') {
            apiPayload['projectId'] = projectId;
        }
        if (typeof resourceType !== 'undefined') {
            apiPayload['resourceType'] = resourceType;
        }
        if (typeof resourceId !== 'undefined') {
            apiPayload['resourceId'] = resourceId;
        }
        if (typeof mode !== 'undefined') {
            apiPayload['mode'] = mode;
        }
        if (typeof reason !== 'undefined') {
            apiPayload['reason'] = reason;
        }
        if (typeof expiredAt !== 'undefined') {
            apiPayload['expiredAt'] = expiredAt;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'managerBlock',
            models: {
                managerBlock: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                        projectName: '',
                        region: '',
                        organizationName: '',
                        organizationId: '',
                        billingPlan: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Deletes resource blocks for a project.
     *
     * @param {string} params.projectId - Project ID
     * @param {BlockResourceType} params.resourceType - Resource type to unblock
     * @param {string} params.resourceId - Optional resource ID (if omitted, all blocks of this type will be removed)
     * @throws {AppwriteException}
     * @returns {Promise<Models.ManagerBlockDelete>}
     */
    deleteBlock(params: {
        projectId: string;
        resourceType: BlockResourceType;
        resourceId?: string;
    }): Promise<Models.ManagerBlockDelete>;
    /**
     * Deletes resource blocks for a project.
     *
     * @param {string} projectId - Project ID
     * @param {BlockResourceType} resourceType - Resource type to unblock
     * @param {string} resourceId - Optional resource ID (if omitted, all blocks of this type will be removed)
     * @throws {AppwriteException}
     * @returns {Promise<Models.ManagerBlockDelete>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteBlock(
        projectId: string,
        resourceType: BlockResourceType,
        resourceId?: string,
    ): Promise<Models.ManagerBlockDelete>;
    deleteBlock(
        paramsOrFirst:
            | {
                  projectId: string;
                  resourceType: BlockResourceType;
                  resourceId?: string;
              }
            | string,
        ...rest: [BlockResourceType?, string?]
    ): Promise<Models.ManagerBlockDelete> {
        let params: {
            projectId: string;
            resourceType: BlockResourceType;
            resourceId?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                projectId: string;
                resourceType: BlockResourceType;
                resourceId?: string;
            };
        } else {
            params = {
                projectId: paramsOrFirst as string,
                resourceType: rest[0] as BlockResourceType,
                resourceId: rest[1] as string,
            };
        }

        const projectId = params.projectId;
        const resourceType = params.resourceType;
        const resourceId = params.resourceId;

        if (typeof projectId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        if (typeof resourceType === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "resourceType"',
            );
        }
        const apiPath = '/manager/blocks';
        const apiPayload: Payload = {};
        if (typeof projectId !== 'undefined') {
            apiPayload['projectId'] = projectId;
        }
        if (typeof resourceType !== 'undefined') {
            apiPayload['resourceType'] = resourceType;
        }
        if (typeof resourceId !== 'undefined') {
            apiPayload['resourceId'] = resourceId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('delete', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'managerBlockDelete',
            models: {
                managerBlockDelete: {
                    defaults: {
                        deleted: 0,
                        blocks: [],
                    },
                    properties: {
                        blocks: {
                            items: 'managerBlock',
                        },
                    },
                },
                managerBlock: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                        projectName: '',
                        region: '',
                        organizationName: '',
                        organizationId: '',
                        billingPlan: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Lists all resource blocks for a project.
     *
     * @param {string} params.projectId - Project ID
     * @throws {AppwriteException}
     * @returns {Promise<Models.ManagerBlockList>}
     */
    listBlocks(params: { projectId: string }): Promise<Models.ManagerBlockList>;
    /**
     * Lists all resource blocks for a project.
     *
     * @param {string} projectId - Project ID
     * @throws {AppwriteException}
     * @returns {Promise<Models.ManagerBlockList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listBlocks(projectId: string): Promise<Models.ManagerBlockList>;
    listBlocks(
        paramsOrFirst: { projectId: string } | string,
    ): Promise<Models.ManagerBlockList> {
        let params: { projectId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { projectId: string };
        } else {
            params = {
                projectId: paramsOrFirst as string,
            };
        }

        const projectId = params.projectId;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        const apiPath = '/manager/blocks/{projectId}'.replace(
            '{projectId}',
            encodeURIComponent(String(projectId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'managerBlockList',
            models: {
                managerBlockList: {
                    defaults: {
                        total: 0,
                        blocks: [],
                    },
                    properties: {
                        blocks: {
                            items: 'managerBlock',
                        },
                    },
                },
                managerBlock: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                        projectName: '',
                        region: '',
                        organizationName: '',
                        organizationId: '',
                        billingPlan: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Clears internal cache.
     *
     * @param {Region} params.region - Target region.
     * @param {CacheTarget} params.cache - Cache target.
     * @param {boolean} params.all - Clear the entire selected cache target.
     * @param {CacheDatabase} params.database - Database cache scope.
     * @param {string} params.projectId - Project ID for project database cache.
     * @param {string} params.collectionId - Collection ID.
     * @param {string} params.documentId - Document ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteCache(params?: {
        region?: Region;
        cache?: CacheTarget;
        all?: boolean;
        database?: CacheDatabase;
        projectId?: string;
        collectionId?: string;
        documentId?: string;
    }): Promise<{}>;
    /**
     * Clears internal cache.
     *
     * @param {Region} region - Target region.
     * @param {CacheTarget} cache - Cache target.
     * @param {boolean} all - Clear the entire selected cache target.
     * @param {CacheDatabase} database - Database cache scope.
     * @param {string} projectId - Project ID for project database cache.
     * @param {string} collectionId - Collection ID.
     * @param {string} documentId - Document ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteCache(
        region?: Region,
        cache?: CacheTarget,
        all?: boolean,
        database?: CacheDatabase,
        projectId?: string,
        collectionId?: string,
        documentId?: string,
    ): Promise<{}>;
    deleteCache(
        paramsOrFirst?:
            | {
                  region?: Region;
                  cache?: CacheTarget;
                  all?: boolean;
                  database?: CacheDatabase;
                  projectId?: string;
                  collectionId?: string;
                  documentId?: string;
              }
            | Region,
        ...rest: [
            CacheTarget?,
            boolean?,
            CacheDatabase?,
            string?,
            string?,
            string?,
        ]
    ): Promise<{}> {
        let params: {
            region?: Region;
            cache?: CacheTarget;
            all?: boolean;
            database?: CacheDatabase;
            projectId?: string;
            collectionId?: string;
            documentId?: string;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst) &&
                ('region' in paramsOrFirst ||
                    'cache' in paramsOrFirst ||
                    'all' in paramsOrFirst ||
                    'database' in paramsOrFirst ||
                    'projectId' in paramsOrFirst ||
                    'collectionId' in paramsOrFirst ||
                    'documentId' in paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                region?: Region;
                cache?: CacheTarget;
                all?: boolean;
                database?: CacheDatabase;
                projectId?: string;
                collectionId?: string;
                documentId?: string;
            };
        } else {
            params = {
                region: paramsOrFirst as Region,
                cache: rest[0] as CacheTarget,
                all: rest[1] as boolean,
                database: rest[2] as CacheDatabase,
                projectId: rest[3] as string,
                collectionId: rest[4] as string,
                documentId: rest[5] as string,
            };
        }

        const region = params.region;
        const cache = params.cache;
        const all = params.all;
        const database = params.database;
        const projectId = params.projectId;
        const collectionId = params.collectionId;
        const documentId = params.documentId;

        const apiPath = '/manager/cache';
        const apiPayload: Payload = {};
        if (typeof region !== 'undefined') {
            apiPayload['region'] = region;
        }
        if (typeof cache !== 'undefined') {
            apiPayload['cache'] = cache;
        }
        if (typeof all !== 'undefined') {
            apiPayload['all'] = all;
        }
        if (typeof database !== 'undefined') {
            apiPayload['database'] = database;
        }
        if (typeof projectId !== 'undefined') {
            apiPayload['projectId'] = projectId;
        }
        if (typeof collectionId !== 'undefined') {
            apiPayload['collectionId'] = collectionId;
        }
        if (typeof documentId !== 'undefined') {
            apiPayload['documentId'] = documentId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Blocks or unblocks an organization, the same way a billing-limit block does. Blocking makes the organization read-only, disables its backup policies and spins down its dedicated databases.
     *
     * @param {string} params.teamId - Organization ID.
     * @param {boolean} params.status - Organization status. Set to `false` to block and `true` to unblock.
     * @param {string} params.reason - Optional reason when blocking, kept in the audit trail.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Organization<Preferences>>}
     */
    updateOrganizationStatus<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        teamId: string;
        status: boolean;
        reason?: string;
    }): Promise<Models.Organization<Preferences>>;
    /**
     * Blocks or unblocks an organization, the same way a billing-limit block does. Blocking makes the organization read-only, disables its backup policies and spins down its dedicated databases.
     *
     * @param {string} teamId - Organization ID.
     * @param {boolean} status - Organization status. Set to `false` to block and `true` to unblock.
     * @param {string} reason - Optional reason when blocking, kept in the audit trail.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Organization<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOrganizationStatus<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        teamId: string,
        status: boolean,
        reason?: string,
    ): Promise<Models.Organization<Preferences>>;
    updateOrganizationStatus<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst:
            { teamId: string; status: boolean; reason?: string } | string,
        ...rest: [boolean?, string?]
    ): Promise<Models.Organization<Preferences>> {
        let params: { teamId: string; status: boolean; reason?: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                teamId: string;
                status: boolean;
                reason?: string;
            };
        } else {
            params = {
                teamId: paramsOrFirst as string,
                status: rest[0] as boolean,
                reason: rest[1] as string,
            };
        }

        const teamId = params.teamId;
        const status = params.status;
        const reason = params.reason;

        if (typeof teamId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "teamId"');
        }
        if (typeof status === 'undefined') {
            throw new AppwriteException('Missing required parameter: "status"');
        }
        const apiPath = '/manager/organizations/status';
        const apiPayload: Payload = {};
        if (typeof teamId !== 'undefined') {
            apiPayload['teamId'] = teamId;
        }
        if (typeof status !== 'undefined') {
            apiPayload['status'] = status;
        }
        if (typeof reason !== 'undefined') {
            apiPayload['reason'] = reason;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'organization',
            models: {
                organization: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        total: 0,
                        budgetAlerts: [75],
                        billingPlan: 'tier-0',
                        billingPlanId: 'tier-0',
                        billingEmail: '',
                        status: 'draft',
                        markedForDeletion: false,
                        platform: 'appwrite',
                        projects: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Updates a console user status using a user ID or email address.
     *
     * @param {boolean} params.status - User status. Set to `false` to block and `true` to unblock.
     * @param {string} params.userId - User ID.
     * @param {string} params.email - User email address.
     * @param {string} params.reason - Optional reason when blocking a user. Accepted for parity with the CLI task but not persisted.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateUserStatus<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        status: boolean;
        userId?: string;
        email?: string;
        reason?: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Updates a console user status using a user ID or email address.
     *
     * @param {boolean} status - User status. Set to `false` to block and `true` to unblock.
     * @param {string} userId - User ID.
     * @param {string} email - User email address.
     * @param {string} reason - Optional reason when blocking a user. Accepted for parity with the CLI task but not persisted.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateUserStatus<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        status: boolean,
        userId?: string,
        email?: string,
        reason?: string,
    ): Promise<Models.User<Preferences>>;
    updateUserStatus<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst:
            | {
                  status: boolean;
                  userId?: string;
                  email?: string;
                  reason?: string;
              }
            | boolean,
        ...rest: [string?, string?, string?]
    ): Promise<Models.User<Preferences>> {
        let params: {
            status: boolean;
            userId?: string;
            email?: string;
            reason?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                status: boolean;
                userId?: string;
                email?: string;
                reason?: string;
            };
        } else {
            params = {
                status: paramsOrFirst as boolean,
                userId: rest[0] as string,
                email: rest[1] as string,
                reason: rest[2] as string,
            };
        }

        const status = params.status;
        const userId = params.userId;
        const email = params.email;
        const reason = params.reason;

        if (typeof status === 'undefined') {
            throw new AppwriteException('Missing required parameter: "status"');
        }
        const apiPath = '/manager/users/status';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof status !== 'undefined') {
            apiPayload['status'] = status;
        }
        if (typeof reason !== 'undefined') {
            apiPayload['reason'] = reason;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'user',
            models: {
                user: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        registration: '',
                        status: true,
                        labels: [],
                        passwordUpdate: '',
                        email: '',
                        phone: '',
                        emailVerification: false,
                        phoneVerification: false,
                        mfa: false,
                        targets: [],
                        accessedAt: '',
                    },
                    properties: {
                        targets: {
                            items: 'target',
                        },
                    },
                },
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }
}
